import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Users } from "lucide-react";
import { SITE } from "../data/site";
import { ctaRoute } from "../data/ctaRoutes";

const CLASSES = [
  {
    title: "Hip-Hop",
    level: "Foundation → Advanced",
    schedule: "Multiple weekly",
    desc: "Groove, bounce and rhythm — the building blocks of street dance culture.",
    img: SITE.classImg1,
    span: "md:col-span-7 md:row-span-2",
    kind: "adult",
  },
  {
    title: "Afro Dance",
    level: "All Levels",
    schedule: "Weekly",
    desc: "High-energy Afrobeats choreography rooted in West African movement.",
    img: SITE.classImg5,
    span: "md:col-span-5",
    kind: "adult",
  },
  {
    title: "Dancehall",
    level: "All Levels",
    schedule: "Weekly",
    desc: "Jamaican dancehall vocab, groundwork and authentic flavour.",
    img: SITE.classImg2,
    span: "md:col-span-4",
    kind: "adult",
  },
  {
    title: "Krump",
    level: "Intermediate",
    schedule: "Weekly",
    desc: "Aggressive, expressive and unapologetically street.",
    img: SITE.classImg6,
    span: "md:col-span-4",
    kind: "adult",
  },
  {
    title: "Amapiano",
    level: "All Levels",
    schedule: "Weekly",
    desc: "The South African sound moving the world — log drum, log dance.",
    img: SITE.classImg7,
    span: "md:col-span-4",
    kind: "adult",
  },
  {
    title: "Freestyle & Battle Training",
    level: "Intermediate → Elite",
    schedule: "Weekly",
    desc: "Cypher mentality. Build your own voice and prepare for the arena.",
    img: SITE.classImg3,
    span: "md:col-span-7",
    kind: "adult",
  },
  {
    title: "Performance Development",
    level: "Audition-based",
    schedule: "Weekly",
    desc: "Stage craft, projection, formations and showcase preparation.",
    img: SITE.classImg4,
    span: "md:col-span-5",
    kind: "adult",
  },
];

export const Classes = () => {
  return (
    <section
      id="classes"
      data-focus="adult"
      data-testid="classes-section"
      className="relative py-24 sm:py-32 bg-gradient-to-b from-black via-zinc-950 to-black"
    >
      {/* Disciplined grid background */}
      <div className="absolute inset-0 grid-lines opacity-25 pointer-events-none" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/40 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        {/* Structured header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12 pb-8 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">01 · Weekly Training Programmes</span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide text-white max-w-3xl leading-[0.95]">
              Train weekly. <span className="text-brand-yellow text-glow-yellow">Perform</span> always.
            </h2>
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <div className="flex items-center gap-5 text-zinc-300">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
                <Clock size={14} className="text-brand-yellow" /> Weekly
              </div>
              <span className="h-3 w-px bg-white/20" />
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
                <Users size={14} className="text-brand-yellow" /> Kids · Youth · Adults
              </div>
            </div>
            <p className="text-zinc-400 text-sm max-w-sm lg:text-right">
              Structured, performance-focused training across <span className="text-white font-semibold">London &amp; Kent</span>.
            </p>
          </div>
        </div>

        {/* Programme bento — sharper edges, structured info row */}
        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[240px] gap-4">
          {CLASSES.map((c, i) => {
            const route = ctaRoute(c.kind === "kids" ? "kids" : "adult", { utm_source: "homepage_classes_card", utm_content: c.title });
            return (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className={c.span}
            >
            <Link
              to={route.to}
              data-testid={`class-card-${i}`}
              className={`group relative overflow-hidden rounded-xl border border-white/10 hover:border-brand-yellow transition-all min-h-[240px] block h-full`}
            >
              <img
                src={c.img}
                alt={c.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 brightness-[0.4] group-hover:brightness-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />

              {/* Top tag row */}
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between gap-3 z-10">
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-yellow bg-black/60 backdrop-blur px-3 py-1.5 rounded-sm border-l-2 border-brand-yellow">
                  {String(i + 1).padStart(2, "0")} · {c.level}
                </span>
                <ArrowUpRight className="text-white/70 group-hover:text-brand-yellow group-hover:rotate-12 transition-all" size={22} />
              </div>

              {/* Bottom block */}
              <div className="relative h-full flex flex-col justify-end p-6 sm:p-7">
                <h3 className="font-display text-3xl sm:text-4xl text-white tracking-wide leading-tight">
                  {c.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-300 max-w-md leading-snug">{c.desc}</p>
                <div className="mt-4 pt-3 border-t border-white/15 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                  <span>{c.schedule}</span>
                  <span className="text-brand-yellow font-bold">Register →</span>
                </div>
              </div>
            </Link>
            </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
